import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaEnvelope,
  FaClock,
  FaFacebook,
  FaTwitter,
  FaLinkedin,
  FaInstagram,
} from "react-icons/fa";
import styles from "./Contact.module.css";
import checkUpImage from "../././assets/contact-us.jpg";

const fadeInUp = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.2 },
  },
};

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const [status, setStatus] = useState({ type: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: "", message: "" });

    console.log("1. Form submitted, data:", formData);

    try {
      console.log("2. Fetching:", "/api/send_contact_email.php");
      const response = await fetch("/api/send_contact_email.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      console.log("3. Response status:", response.status);
      console.log("4. Response OK?", response.ok);

      // Get raw text first
      const text = await response.text();
      console.log("5. Raw response text:", text);

      let result;
      try {
        result = JSON.parse(text);
        console.log("6. Parsed result:", result);
      } catch (parseError) {
        console.error("JSON parse error:", parseError);
        setStatus({
          type: "error",
          message: "Server returned invalid response. Please try again.",
        });
        setLoading(false);
        return;
      }

      if (result.success) {
        setStatus({ type: "success", message: result.message });
        setFormData({ name: "", email: "", subject: "", message: "" });
        setTimeout(() => setStatus({ type: "", message: "" }), 5000);
      } else {
        setStatus({
          type: "error",
          message: result.message || "Something went wrong.",
        });
      }
    } catch (error) {
      console.error("Fetch error:", error);
      setStatus({ type: "error", message: `Network error: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: <FaMapMarkerAlt />,
      title: "Visit Us",
      details: "P.O. Box 5687 00200, Nairobi, Kenya",
      link: "https://maps.google.com/?q=Nairobi+Kenya",
    },
    {
      icon: <FaPhoneAlt />,
      title: "Call Us",
      details: "+254 722 265791",
      link: "tel:+254722265791",
    },
    {
      icon: <FaEnvelope />,
      title: "Email Us",
      details: "info@muigaiclinic.com",
      link: "mailto:info@muigaiclinic.com",
    },
    {
      icon: <FaClock />,
      title: "Working Hours",
      details: "Mon-Fri: 8am–6pm<br />Sat: 9am–4pm<br />Sun & Holidays: Closed",
      link: null,
    },
  ];

  const socialLinks = [
    {
      icon: <FaFacebook />,
      url: "https://facebook.com/your-clinic",
      name: "Facebook",
    },
    {
      icon: <FaTwitter />,
      url: "https://twitter.com/your-clinic",
      name: "Twitter",
    },
    {
      icon: <FaLinkedin />,
      url: "https://linkedin.com/company/your-clinic",
      name: "LinkedIn",
    },
    {
      icon: <FaInstagram />,
      url: "https://instagram.com/your-clinic",
      name: "Instagram",
    },
  ];

  return (
    <>
      <Helmet>
        <title>Contact Us | Muigai Medical Center</title>
        <meta
          name="description"
          content="Get in touch with Muigai Medical Center. Find our location, working hours, and contact details. Book an appointment or send us a message."
        />
        <meta
          name="keywords"
          content="contact, location, phone, email, Nairobi medical center"
        />
        <meta property="og:title" content="Contact Muigai Medical Center" />
        <meta
          property="og:description"
          content="Reach out to us for appointments, inquiries, or emergencies."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://muigaiclinic.com/contact" />
        <meta property="og:image" content="/assets/contact-og.jpg" />
        <link rel="canonical" href="https://muigaiclinic.com/contact" />
      </Helmet>

      {/* Hero Banner */}
      <section
        style={{ backgroundImage: `url(${checkUpImage})` }}
        className={styles.heroBanner}
      >
        <div className={styles.heroOverlay}></div>
        <div className="container">
          <motion.div
            className={styles.heroContent}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <motion.div className={styles.sectionTag} variants={fadeInUp}>
              GET IN TOUCH
            </motion.div>
            <motion.h1 variants={fadeInUp}>Contact Us</motion.h1>
            <motion.p className={styles.heroSubtitle} variants={fadeInUp}>
              We're here to help. Reach out for appointments, questions, or
              emergencies.
            </motion.p>
          </motion.div>
        </div>
        <div className={styles.heroBackground}>
          <div
            className={styles.floatingCircle}
            style={{ top: "15%", left: "10%", width: "120px", height: "120px" }}
          />
          <div
            className={styles.floatingCircle}
            style={{
              bottom: "20%",
              right: "15%",
              width: "80px",
              height: "80px",
            }}
          />
          <div
            className={styles.floatingCircle}
            style={{ top: "60%", left: "20%", width: "60px", height: "60px" }}
          />
        </div>
      </section>

      {/* Contact Cards */}
      <section className={styles.cardsSection}>
        <div className="container">
          <div className={styles.cardsGrid}>
            {contactInfo.map((item, idx) => (
              <div key={idx} className={styles.contactCard}>
                <div className={styles.cardIcon}>{item.icon}</div>
                <h3>{item.title}</h3>
                {item.link ? (
                  <a
                    href={item.link}
                    target={item.link.startsWith("http") ? "_blank" : "_self"}
                    rel="noopener noreferrer"
                  >
                    <p dangerouslySetInnerHTML={{ __html: item.details }} />
                  </a>
                ) : (
                  <p dangerouslySetInnerHTML={{ __html: item.details }} />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form and Map Section */}
      <section className={styles.formMapSection}>
        <div className="container">
          <div className={styles.formMapGrid}>
            {/* Contact Form */}
            <div className={styles.formWrapper}>
              <h2>Send Us a Message</h2>
              {status.message && (
                <div
                  className={
                    status.type === "success"
                      ? styles.successMessage
                      : styles.errorMsg
                  }
                >
                  {status.message}
                </div>
              )}
              <form onSubmit={handleSubmit} className={styles.contactForm}>
                <div className={styles.formGroup}>
                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    disabled={loading}
                  />
                </div>
                <div className={styles.formGroup}>
                  <input
                    type="email"
                    name="email"
                    placeholder="Your Email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    disabled={loading}
                  />
                </div>
                <div className={styles.formGroup}>
                  <input
                    type="text"
                    name="subject"
                    placeholder="Subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    disabled={loading}
                  />
                </div>
                <div className={styles.formGroup}>
                  <textarea
                    name="message"
                    rows="5"
                    placeholder="Your Message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    disabled={loading}
                  ></textarea>
                </div>
                <button
                  type="submit"
                  className={styles.submitBtn}
                  disabled={loading}
                >
                  {loading ? "Sending..." : "Send Message"}
                </button>
              </form>
            </div>

            {/* Google Map */}
            <div className={styles.mapWrapper}>
              <h2>Find Us</h2>
              <div className={styles.mapContainer}>
                <iframe
                  title="Muigai Medical Center Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3988.819618542634!2d36.82172231428406!3d-1.292091599068663!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x182f10c5b5b5b5b5%3A0x5b5b5b5b5b5b5b5b!2sNairobi%2C%20Kenya!5e0!3m2!1sen!2ske!4v1234567890123!5m2!1sen!2ske"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Media Section */}
      <section className={styles.socialSection}>
        <div className="container">
          <h2>Connect With Us</h2>
          <div className={styles.socialLinks}>
            {socialLinks.map((social, idx) => (
              <a
                key={idx}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.socialLink}
                aria-label={social.name}
              >
                {social.icon}
                <span>{social.name}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Emergency CTA */}
      <section className={styles.emergencySection}>
        <div className="container">
          <div className={styles.emergencyCard}>
            <FaPhoneAlt className={styles.emergencyIcon} />
            <h3>Emergency? We're Here 24/7</h3>
            <p>For urgent medical assistance, please call us immediately.</p>
            <a href="tel:+254722265791" className={styles.emergencyButton}>
              +254 722 265791
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
